import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { useClerk, useSignIn } from '@clerk/clerk-react';
import {
  Button,
  isPasswordValid,
  PageHeader,
  PasswordConditions,
  Textfield,
} from '@flexo/atoms';
import {
  httpService,
  IconWrapper,
  REGEX,
  triggerKeychainSave,
} from '@flexo/general';
import {
  useFormValidation,
  useKeyboardAwareScroll,
  usePrivacyPolicy,
} from '@flexo/hooks';
import { ALERT } from '@flexo/reducers';
import { Player } from '@lottiefiles/react-lottie-player';
import React, { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { KeyboardAwareLayout } from '../keyboard-aware-layout';
import styles from './register.module.scss';

const ENV = (import.meta as any).env;

export enum RegisterStage {
  Stage_1 = 'Stage_1',
  Stage_2 = 'Stage_2',
}

interface InputConfig {
  name: string;
  label: string;
  placeholder: string;
  type: string;
  action?: string;
  errorDescription?: string;
}

export function RegisterOOBcode(props) {
  const {
    Register__Header,
    Register__Inputs,
    Register__Footer,
    Register__Footer__Register,
    Register__Footer__Register__Button,
    Register__Privacy,
    Register__Privacy__Checkbox,
    Register__SupervisorWarning,
  } = styles;
  const { containedLabel, textfieldColor, inputs, supervisorWarning } = props;
  const loader = '/loaderLottie.json';
  const accountCreationFormId = 'account-creation-password-form';
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { signIn, setActive } = useSignIn();
  const clerk = useClerk();

  const PRIVACY_AND_TERMS = t('pages.register.privacy_and_terms', {
    returnObjects: true,
  });
  const navigate = useNavigate();

  const [currentStage, setCurrentStage] = useState<RegisterStage>(
    RegisterStage.Stage_1
  );
  const [registerData, setRegisterData] = useState({
    email: '',
    billNumber: '',
    customerNumber: '',
    password: '',
    confirmPassword: '',
  });

  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  // FLEXO-6074 (reg#5): inline billing-guide modal for the web register flow.
  const [showBillModal, setShowBillModal] = useState<boolean>(false);
  const [inputType, setInputType] = useState({
    password: 'password',
    confirmPassword: 'password',
  });

  const [icon, setIcon] = useState({
    password: 'visibility',
    confirmPassword: 'visibility',
  });
  const [buttonLoading, setButtonLoading] = useState(false);
  const { policy } = usePrivacyPolicy<Record<string, unknown>>();

  const { keyboardVisible } = useKeyboardAwareScroll();
  const isKwickWebRegister =
    ENV?.VITE_APP_IS_WEB === 'true' && props?.devID === 'register-web-kwick';

  // Email validation function
  const isEmail = (email: string) => {
    return REGEX.email.test(email);
  };

  // Form validation hook
  const {
    errors,
    blur,
    passwordBlur,
    passwordValidation,
    handleBlur,
    handleFocus,
    handlePasswordValidation,
  } = useFormValidation(
    {
      fields: [
        'email',
        'billNumber',
        'customerNumber',
        'password',
        'confirmPassword',
      ],
      validationRules: {
        email: {
          validate: (value) => isEmail(value),
        },
      },
      hasPasswordFields: true,
    },
    registerData
  );

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setRegisterData({ ...registerData, [name]: value });

    if (name === 'password') {
      handlePasswordValidation(value, registerData.confirmPassword);
    }
    if (name === 'confirmPassword') {
      handlePasswordValidation(registerData.password, value);
    }
  };
  const togglePasswordVisibility = (name: string) => {
    setInputType((prevState) => ({
      ...prevState,
      [name]: prevState[name] === 'password' ? 'text' : 'password',
    }));
    setIcon((prevIcon) => ({
      ...prevIcon,
      [name]:
        prevIcon[name] === 'visibility' ? 'hide-visibility' : 'visibility',
    }));
  };

  const onNext = async () => {
    setButtonLoading(true);
    try {
      const response = await httpService.post({
        url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/registrations/verify`,
        data: {
          organizationId: ENV.VITE_APP_ORG_ID,
          invoiceNo: registerData.billNumber,
          clientID: registerData.customerNumber,
        },
        skipLogoutOn401: true,
      });

      if (response.status === 200 && response.data?.valid) {
        // CM-5110 #4: when the backend flags an existing app account for this
        // invoice/customer pair, stop at Stage 1 and divert to login/password
        // recovery — completing Stage 2 would return success while silently
        // discarding the newly chosen password. hasAccount is additive and may
        // be absent (older backend) or null (Clerk degraded): only an explicit
        // true blocks the flow.
        if (response.data?.hasAccount === true) {
          dispatch(
            ALERT.SetAlert({
              value: {
                type: 'BANNER_ACCOUNT_ALREADY_REGISTERED',
              },
            } as any)
          );
          setButtonLoading(false);
          return;
        }
        setCurrentStage(RegisterStage.Stage_2);
        setButtonLoading(false);
      } else {
        dispatch(
          ALERT.SetAlert({
            value: {
              type: 'BANNER_INVALID_REGISTRATION_DATA',
            },
          } as any)
        );
        setButtonLoading(false);
      }
    } catch (error: any) {
      setButtonLoading(false);
      dispatch(
        ALERT.SetAlert({
          value: {
            type: 'BANNER_UNEXPECTED_ERROR',
          },
        } as any)
      );
      console.error('Error verifying user:', error);
    }
  };

  const autoLoginWithClerk = async () => {
    try {
      if (!signIn || !setActive) {
        throw new Error('Clerk signIn not available');
      }

      // Wait for Clerk to propagate the newly created user
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const loginResult = await signIn.create({
        identifier: registerData.email,
        password: registerData.password,
      });

      if (loginResult.status !== 'complete') {
        throw new Error('Auto-login did not complete');
      }

      await setActive({ session: loginResult.createdSessionId });
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Set active organization via useClerk()
      await new Promise((resolve) => setTimeout(resolve, 300));
      const activeUser = clerk.user;
      const orgMemberships = activeUser?.organizationMemberships || [];
      const targetOrgId = ENV.VITE_APP_CLERK_ORG_ID;
      const orgMembership = targetOrgId
        ? orgMemberships.find((m: any) => m.organization.id === targetOrgId) || orgMemberships[0]
        : orgMemberships[0];

      if (orgMembership?.organization) {
        await clerk.setActive({ organization: orgMembership.organization.id });

        // Verify session is no longer pending
        await new Promise((resolve) => setTimeout(resolve, 300));
        const updatedStatus = clerk.session?.status;
        if (updatedStatus === 'pending') {
          try { await setActive({ session: null }); } catch (_) { /* noop */ }
          throw new Error('Session stuck in pending state');
        }
      }

      // FLEXO-6074: self-registered users are always end-users. On Kwick WEB,
      // VITE_APP_FIRST_PAGE is the admin landing ("communities") whose admin-only
      // resolvers reject the end-user role with 403 ERR_AUTH_001. Route the web
      // end-user to their own member aggregate instead, mirroring the authenticated
      // catch-all redirect in app.tsx (/member/:flexoUserId). The mobile flow
      // (isKwickWebRegister === false) keeps the original VITE_APP_FIRST_PAGE
      // navigation unchanged for backward compatibility with the kwick app.
      // NOTE: single-site → /site/:siteId refinement is intentionally NOT done here
      // (entities aren't loaded at auto-login time); it belongs to the centralised
      // post-auth landing resolver.
      const flexoUserId = clerk.user?.publicMetadata?.flexo_user_id as
        | string
        | undefined;
      if (isKwickWebRegister && flexoUserId) {
        navigate(`/member/${flexoUserId}`);
      } else {
        navigate(`/${ENV.VITE_APP_FIRST_PAGE}`);
      }
    } catch (error: any) {
      console.error('Auto-login after registration failed:', error);
      // Fall back to login page
      dispatch(
        ALERT.SetAlert({
          value: {
            type: 'BANNER_UNEXPECTED_ERROR',
          },
        } as any)
      );
      setTimeout(() => {
        dispatch(ALERT.ResetAlert({} as any));
        navigate('/login');
      }, 3000);
    }
  };

  const completeRegistration = async () => {
    setButtonLoading(true);
    try {
      const response = await httpService.post({
        url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/registrations`,
        data: {
          organizationId: ENV.VITE_APP_ORG_ID,
          clientID: registerData.customerNumber,
          invoiceNo: registerData.billNumber,
          email: registerData.email,
          password: registerData.password,
        },
        skipLogoutOn401: true,
      });

      if (response.status === 200 && response.data?.success) {
        triggerKeychainSave(registerData.email, registerData.password);
        autoLoginWithClerk();
      } else {
        const alertType =
          response.status === 400
            ? 'BANNER_WRONG_REQUEST_DATA'
            : 'BANNER_UNEXPECTED_ERROR';

        dispatch(
          ALERT.SetAlert({
            value: {
              type: alertType,
            },
          } as any)
        );
        setButtonLoading(false);
        console.error(
          'Registration failed:',
          response.status,
          response.statusText
        );
      }
    } catch (error: any) {
      setButtonLoading(false);
      dispatch(
        ALERT.SetAlert({
          value: {
            type: 'BANNER_UNEXPECTED_ERROR',
          },
        } as any)
      );
      console.error('Error completing registration:', error);
    }
  };

  const openExternal = async (url: string) => {
    try {
      await Browser.open({ url });
    } catch {
      window.open(url, '_blank');
    }
  };

  const openBillExample = async () => {
    // FLEXO-6074 (reg#5): on Kwick web, show the billing guide inline (image modal),
    // mirroring the mobile register flow, instead of opening the PDF in a popup tab.
    if (isKwickWebRegister) {
      setShowBillModal(true);
      return;
    }
    try {
      // Fetch the PDF from the public folder
      const response = await fetch('/billExample.pdf');
      const blob = await response.blob();

      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(blob);

      reader.onloadend = async () => {
        const base64data = reader.result as string;
        const raw = base64data.includes('base64,')
          ? base64data.split('base64,')[1]
          : base64data;

        const { uri } = await Filesystem.writeFile({
          path: `invoices/bill_example_${Date.now()}.pdf`,
          data: raw,
          directory: Directory.Documents,
          recursive: true,
        });

        const platform = Capacitor.getPlatform();
        const fullUri = uri.startsWith('file://') ? uri : `file://${uri}`;

        if (platform === 'ios') {
          // ✅ Quick Look (CapAwesome) → full-screen, pinch zoom + native Done
          const modA: any = await import(
            /* @vite-ignore */ '@capawesome-team/capacitor-file-opener'
          );
          const FOA = modA?.FileOpener ?? modA?.default ?? modA;

          if (FOA?.openFile) {
            try {
              await FOA.openFile({
                path: fullUri,
                mimeType: 'application/pdf',
                displayName: 'Bill Example',
              });
              console.log('[PDF] iOS Quick Look via CapAwesome');
              return;
            } catch {
              await FOA.openFile({
                path: uri,
                mimeType: 'application/pdf',
                displayName: 'Bill Example',
              });
              console.log('[PDF] iOS Quick Look via CapAwesome (no scheme)');
              return;
            }
          }

          if (FOA?.open) {
            try {
              await FOA.open({
                filePath: fullUri,
                contentType: 'application/pdf',
              });
              console.log('[PDF] iOS Quick Look via CapAwesome (open)');
              return;
            } catch {
              await FOA.open({ filePath: uri, contentType: 'application/pdf' });
              console.log(
                '[PDF] iOS Quick Look via CapAwesome (open, no scheme)'
              );
              return;
            }
          }

          console.warn('[PDF] CapAwesome not available on iOS');
          return;
        }

        // Android: open with intent (Community as fallback)
        try {
          const modA: any = await import(
            /* @vite-ignore */ '@capawesome-team/capacitor-file-opener'
          );
          const FOA = modA?.FileOpener ?? modA?.default ?? modA;
          if (FOA?.openFile) {
            await FOA.openFile({ path: fullUri, mimeType: 'application/pdf' });
            return;
          }
          if (FOA?.open) {
            await FOA.open({
              filePath: fullUri,
              contentType: 'application/pdf',
            });
            return;
          }
        } catch (error) {
          console.warn(
            '[BillExample] Failed to open PDF via CapAwesome FileOpener',
            error
          );
        }

        // web/dev fallback
        const bin = atob(raw);
        const buf = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
        const pdfBlob = new Blob([buf], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank', 'noopener,noreferrer');
      };
    } catch (error) {
      console.error('[BillExample] Error opening PDF:', error);
    }
  };

  const handleActionClick = (action: string) => {
    if (
      action === 'pages.register.bill_number_action' ||
      action === 'pages.register.customer_number_action'
    ) {
      // Open the bill example PDF to help users find their bill/customer number
      openBillExample();
    }
  };

  const handleBack = () => {
    if (currentStage === RegisterStage.Stage_1) {
      navigate('/login', { replace: true });
    } else {
      setCurrentStage(RegisterStage.Stage_1);
    }
  };

  // Define header component
  const headerComponent = (
    <div className={Register__Header}>
      <PageHeader
        title={
          currentStage === RegisterStage.Stage_1
            ? t('pages.register.title')
            : t('pages.account_creation.title')
        }
        subtitle={
          currentStage === RegisterStage.Stage_1
            ? t('pages.register.subtitle')
            : t('pages.account_creation.subtitle')
        }
        back={
          currentStage === RegisterStage.Stage_1
            ? (t('pages.register.go_to_login') as string)
            : (t('pages.register.get_back') as string)
        }
        backAction={handleBack}
      />
    </div>
  );

  const webHeaderComponent = (
    <div className={Register__Header}>
      <div className={styles.Register__Web__Logo}>
        <IconWrapper iconName="logo_wide" />
      </div>
      <h1 className={styles.Register__Web__Title}>
        {currentStage === RegisterStage.Stage_1
          ? t('pages.register.title')
          : t('pages.account_creation.title')}
      </h1>
      <p className={styles.Register__Web__Subtitle}>
        {currentStage === RegisterStage.Stage_1
          ? t('pages.register.subtitle')
          : t('pages.account_creation.subtitle')}
      </p>
    </div>
  );

  // Comprehensive function to check if submit button should be disabled
  const isDisabled = () => {
    // Always disable when loading
    if (buttonLoading) return true;

    if (currentStage === RegisterStage.Stage_1) {
      // Stage 1: Verify user with bill and customer number
      return (
        !registerData.billNumber.length || !registerData.customerNumber.length
      );
    }

    if (currentStage === RegisterStage.Stage_2) {
      // Stage 2: Create account with password
      return (
        !registerData.password ||
        !registerData.confirmPassword ||
        !isPasswordValid(registerData.password) ||
        !passwordValidation.passwordsMatch ||
        !termsAccepted
      );
    }

    return false;
  };

  const buttonLabel =
    currentStage === RegisterStage.Stage_1
      ? (t('pages.register.next') as string)
      : (t('pages.register.submit') as string);

  const buttonContent = (
    <span className={styles.Register__ButtonLabel}>
      <span
        className={
          buttonLoading ? styles.Register__ButtonLabel__HiddenText : undefined
        }
      >
        {buttonLabel}
      </span>
      {buttonLoading ? (
        <span className={styles.Register__ButtonLabel__Loader}>
          <Player
            autoplay
            loop
            src={loader}
            style={{ height: 20, width: 60 }}
          />
        </span>
      ) : null}
    </span>
  );

  // Define footer component
  const footerComponent = (
    <div className={Register__Footer}>
      <Button
        size="large"
        type={currentStage === RegisterStage.Stage_2 ? 'submit' : 'button'}
        form={
          currentStage === RegisterStage.Stage_2
            ? accountCreationFormId
            : undefined
        }
        onClick={currentStage === RegisterStage.Stage_1 ? onNext : undefined}
        label={buttonContent}
        variant="primary"
        color="secondary"
        disabled={isDisabled()}
      />

      <div
        className={` paragraph ${Register__Footer__Register}`}
        style={{ minHeight: keyboardVisible ? '0' : '38px' }}
      >
        {currentStage === RegisterStage.Stage_1 && !keyboardVisible ? (
          <div style={{ marginTop: '16px' }}>
            {t('pages.register.already_registered')}{' '}
            <button
              className={` paragraph ${Register__Footer__Register__Button}`}
              onClick={() => navigate('/login', { replace: true })}
            >
              {t('pages.register.go_to_login')}
            </button>
          </div>
        ) : (
          <div aria-hidden="true"></div>
        )}
      </div>
    </div>
  );

  const webFooterComponent = (
    <div className={Register__Footer}>
      <button
        type="button"
        className={styles.Register__Web__BackButton}
        onClick={handleBack}
      >
        {currentStage === RegisterStage.Stage_1
          ? (t('pages.register.go_to_login') as string)
          : (t('pages.register.get_back') as string)}
      </button>

      <Button
        size="large"
        type={currentStage === RegisterStage.Stage_2 ? 'submit' : 'button'}
        form={
          currentStage === RegisterStage.Stage_2
            ? accountCreationFormId
            : undefined
        }
        onClick={currentStage === RegisterStage.Stage_1 ? onNext : undefined}
        label={buttonContent}
        variant="primary"
        color="secondary"
        disabled={isDisabled()}
      />
    </div>
  );

  // Define main content
  const FormWrapper = currentStage === RegisterStage.Stage_2 ? 'form' : 'div';

  const mainContent = (
    <FormWrapper
      {...(currentStage === RegisterStage.Stage_2
        ? { id: accountCreationFormId }
        : {})}
      className={styles.Register__Content}
      {...(currentStage === RegisterStage.Stage_2 ? {
        onSubmit: (e: React.FormEvent) => {
          e.preventDefault();
          if (!isDisabled()) completeRegistration();
        },
      } : {})}
    >
      {supervisorWarning && currentStage === RegisterStage.Stage_1 ? (
        <div className={Register__SupervisorWarning} role="note">
          {isKwickWebRegister ? (
            <IconWrapper
              iconName="info"
              className={styles.Register__SupervisorWarning__Icon}
            />
          ) : null}
          <p>{t(supervisorWarning) as string}</p>
        </div>
      ) : null}
      <div className={Register__Inputs}>
        {inputs?.[currentStage]?.map((input: InputConfig) => {
          // Map field names to literal values for keychain/password manager compatibility
          const getHtmlName = (fieldName: string) => {
            if (fieldName === 'email') return 'email';
            if (fieldName === 'password') return 'password';
            if (fieldName === 'confirmPassword') return 'confirmPassword';
            return fieldName;
          };

          // Map autocomplete values for keychain compatibility
          const getAutoComplete = (fieldName: string, type: string) => {
            if (fieldName === 'email') return 'username';
            if (fieldName === 'password') return 'new-password';
            if (fieldName === 'confirmPassword') return 'off';
            return 'off';
          };

          return (
            <div key={input.name} className={styles.Register_Inputs_Input}>
              <Textfield
                variant={containedLabel ? 'containedLabel' : 'default'}
                textfieldColor={textfieldColor || 'white'}
                name={getHtmlName(input.name)}
                label={t(input.label) as string}
                value={registerData[input.name]}
                placeholder={t(input.placeholder) as string}
                onChange={handleInputChange}
                onBlur={handleBlur}
                type={
                  input.type === 'password' ? inputType[input.name] : input.type
                }
                error={errors[input.name] && blur[input.name]}
                containedLabel={containedLabel}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                autoComplete={getAutoComplete(input.name, input.type)}
                icon={input.type === 'password' ? icon[input.name] : ''}
                onIconClick={togglePasswordVisibility}
                action={input.action ? (t(input.action) as string) : undefined}
                onActionClick={
                  input.action
                    ? () => {
                      const action = input.action;
                      if (action) handleActionClick(action);
                    }
                    : undefined
                }
                errorDescription={
                  input.errorDescription
                    ? (t(input.errorDescription) as string)
                    : undefined
                }
                onFocus={handleFocus}
              />
              {input.name === 'password' && (
                <PasswordConditions
                  validation={passwordValidation}
                  isFocused={!passwordBlur}
                  conditionClassName={styles.Register__Conditions}
                  style={{ marginTop: '1.5em' }}
                />
              )}
            </div>
          );
        })}
      </div>
      {currentStage === RegisterStage.Stage_2 && (
        <div
          className={Register__Privacy}
          onClick={() => setTermsAccepted(!termsAccepted)}
        >
          <input
            type="checkbox"
            onChange={() => setTermsAccepted(!termsAccepted)}
            className={Register__Privacy__Checkbox}
            checked={termsAccepted}
          />
          <span className={`${styles.Register__Privacy__Copy} paragraph`}>
            {PRIVACY_AND_TERMS[0]}{' '}
            <span
              className={'color-primary-500'}
              onClick={() =>
                openExternal(policy?.terms_and_conditions_url as string)
              }
            >
              {PRIVACY_AND_TERMS[1]}
            </span>{' '}
            {PRIVACY_AND_TERMS[2]}{' '}
            <span
              className={'color-primary-500'}
              onClick={() => openExternal(policy?.privacy_policy_url as string)}
            >
              {PRIVACY_AND_TERMS[3]}
            </span>
          </span>
        </div>
      )}
    </FormWrapper>
  );

  if (isKwickWebRegister) {
    return (
      <div className={`${styles.Register} ${styles.Register__Web}`}>
        {showBillModal && (
          <div
            className={styles.BillExampleModal}
            onClick={() => setShowBillModal(false)}
          >
            <div
              className={styles.BillExampleModal__Content}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className={styles.BillExampleModal__Close}
                aria-label={t('pages.register.get_back') as string}
                onClick={() => setShowBillModal(false)}
              >
                <IconWrapper iconName="cross" />
              </button>
              <img
                className={styles.BillExampleModal__Image}
                src="/billExample.png"
                alt={t('pages.register.bill_number_action') as string}
              />
            </div>
          </div>
        )}
        {webHeaderComponent}
        <div className={styles.Register__Main}>{mainContent}</div>
        {webFooterComponent}
      </div>
    );
  }

  return (
    <div className={styles.Register}>
      <KeyboardAwareLayout
        header={headerComponent}
        footer={footerComponent}
        extraPadding={0}
        contentClassName={styles.Register__Main}
        additionalLabelFoter={true}
      >
        {mainContent}
      </KeyboardAwareLayout>
    </div>
  );
}

export default RegisterOOBcode;
